import { useContext, useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { StatsigContext } from "../../elements-statsig-provider/statsig-context";

/**
 * Controls visibility of the Promoted-by-Host banner on the Host Hub. Combines
 * the `promoted_by_host` Statsig feature gate with a per-user localStorage flag
 * so a dismissal persists across refreshes for the current campaign.
 */
const useHostHubPromotedByHost = () => {
  const ssoId = useSelector(selectProfileSSOid);
  const { featureGates } = useContext(StatsigContext);
  const isEnabled = Boolean(
    (featureGates as { promoted_by_host?: boolean })?.promoted_by_host,
  );

  const storageKey = ssoId ? `hostHub:promotedByHost:dismissed:${ssoId}` : null;

  // `null` means "not yet read from localStorage"; keeping the banner hidden
  // until we know the real value prevents a previously-dismissed banner from
  // flashing in on the render where ssoId first populates.
  const [isDismissed, setIsDismissed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!storageKey) return;
    try {
      setIsDismissed(window.localStorage.getItem(storageKey) === "true");
    } catch {
      setIsDismissed(false);
    }
  }, [storageKey]);

  const dismiss = () => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, "true");
    } catch {
      // Ignore: quota / private-mode failures shouldn't break the page.
    }
    setIsDismissed(true);
  };

  return {
    isVisible: Boolean(storageKey) && isEnabled && isDismissed === false,
    dismiss,
  };
};

export default useHostHubPromotedByHost;
